// V1.5+ Slice 3 — full-viewport edge-flash overlay. Triggered by
// CriticalFlipBanner / Sfx when a critical flip lands on the viewer's
// side. CSS-only — no canvas, no animation library — so the overlay
// composites cheaply over Phaser's WebGL canvas.
//
// Throttled to one flash per FLASH_THROTTLE_MS so a multi-event tick
// (Cut Link landing → mini_turret_dark → relay_isolated cascading in
// the same frame) doesn't strobe. The throttle lives in the overlay
// itself (not in callers) so every emit site stays a one-line trigger.
//
// Pointer events are disabled on the overlay so the player's input
// passes through to the game canvas underneath.

const ROOT_ID = 'critical-flip-flash';
const STYLE_ID = 'critical-flip-flash-styles';
const FLASH_DURATION_MS = 600;
const FLASH_THROTTLE_MS = 800;

export class ScreenFlash {
  private root: HTMLDivElement | null = null;
  private lastFlashAtMs = -Infinity;
  private clearTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    injectStyles();
    this.mount();
  }

  // Trigger a single flash. Calls within FLASH_THROTTLE_MS are dropped
  // so a stack of three rising-edge events in the same tick produces
  // one perceptual cue, not three strobes.
  flash(): void {
    if (this.root === null) return;
    const now = Date.now();
    if (now - this.lastFlashAtMs < FLASH_THROTTLE_MS) return;
    this.lastFlashAtMs = now;
    // Restart the CSS keyframe animation: clear the active class, force
    // a reflow, then re-add. Without the reflow the second flash on a
    // throttled trigger would never re-trigger the keyframe.
    this.root.classList.remove('critical-flip-flash--active');
    void this.root.offsetWidth;
    this.root.classList.add('critical-flip-flash--active');
    if (this.clearTimer !== null) clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => {
      this.root?.classList.remove('critical-flip-flash--active');
      this.clearTimer = null;
    }, FLASH_DURATION_MS);
  }

  // V2 Tempo — heavyweight match-end "punch." Bigger flash, longer hold,
  // tinted blue (viewer win) or red (viewer loss). Bypasses the throttle
  // because the match only ends once.
  punch(opts: { won: boolean } = { won: true }): void {
    if (this.root === null) return;
    this.lastFlashAtMs = Date.now();
    this.root.classList.remove('critical-flip-flash--active', 'critical-flip-flash--punch-win', 'critical-flip-flash--punch-loss');
    void this.root.offsetWidth;
    this.root.classList.add(opts.won ? 'critical-flip-flash--punch-win' : 'critical-flip-flash--punch-loss');
    if (this.clearTimer !== null) clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => {
      this.root?.classList.remove('critical-flip-flash--punch-win', 'critical-flip-flash--punch-loss');
      this.clearTimer = null;
    }, 900);
  }

  dispose(): void {
    if (this.clearTimer !== null) {
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }
    if (this.root !== null) {
      this.root.classList.remove('critical-flip-flash--active');
    }
  }

  private mount(): void {
    this.root = ensureRoot();
    this.root.classList.remove('critical-flip-flash--active');
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  div.className = 'critical-flip-flash';
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  // Edge-vignette gradient: red at the four viewport edges, transparent
  // at the centre, so the player feels "something just hit me at the
  // edges" without obscuring the world. The keyframe animates opacity
  // over FLASH_DURATION_MS — fade-in fast (12% of the duration), hold
  // briefly, fade-out the rest.
  style.textContent = `
#${ROOT_ID} {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  opacity: 0;
  z-index: 30;
  background:
    radial-gradient(
      ellipse at center,
      rgba(226, 84, 60, 0) 55%,
      rgba(226, 84, 60, 0.35) 100%
    );
}
#${ROOT_ID}.critical-flip-flash--active {
  animation: critical-flip-flash-anim ${FLASH_DURATION_MS}ms ease-out;
}
/* V2 Tempo — match-end "punch" overlay. Center-bright flash + edge
 * vignette tinted to the outcome (cyan win / red loss). Lives 900 ms
 * so it can land BEFORE the announcer banner peaks. */
#${ROOT_ID}.critical-flip-flash--punch-win {
  background:
    radial-gradient(ellipse at center,
      rgba(174, 240, 255, 0.85) 0%,
      rgba(30, 167, 255, 0.55) 35%,
      rgba(30, 167, 255, 0) 75%);
  animation: critical-flip-flash-punch 900ms cubic-bezier(0.16, 0.84, 0.44, 1);
}
#${ROOT_ID}.critical-flip-flash--punch-loss {
  background:
    radial-gradient(ellipse at center,
      rgba(255, 200, 175, 0.75) 0%,
      rgba(255, 42, 42, 0.55) 35%,
      rgba(120, 18, 18, 0) 75%);
  animation: critical-flip-flash-punch 900ms cubic-bezier(0.16, 0.84, 0.44, 1);
}
@keyframes critical-flip-flash-anim {
  0%   { opacity: 0; }
  12%  { opacity: 1; }
  35%  { opacity: 0.65; }
  100% { opacity: 0; }
}
@keyframes critical-flip-flash-punch {
  0%   { opacity: 0; }
  10%  { opacity: 1; }
  35%  { opacity: 0.85; }
  100% { opacity: 0; }
}
`;
  document.head.appendChild(style);
}
